import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const FORM_URL = 'https://readdy.ai/api/form/d726ireuimmp87kg1v50';
const WHATSAPP_URL = 'https://wa.me/31624359162?text=Hi%20Shubam%2C%20ik%20wil%20graag%20meer%20informatie%20over%20inkopen%20uit%20China.';
const SCROLL_POPUP_KEY = 'duc_scroll_popup_shown';
const TIMER_POPUP_KEY = 'duc_timer_popup_shown';
const POPUP_DISABLED_KEY = 'duc_popup_disabled';

function isDisabled() {
  try { return sessionStorage.getItem(POPUP_DISABLED_KEY) === '1'; } catch { return false; }
}

function WhatsAppButton() {
  return (
    <a href={WHATSAPP_URL} target="_blank" rel="nofollow noreferrer" aria-label="Contact via WhatsApp" className="fixed bottom-6 left-4 md:left-6 z-[9980] group">
      <div className="relative">
        <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-[#25D366] cursor-pointer transition-transform duration-200 group-hover:scale-110">
          <i className="ri-whatsapp-line text-white text-xl md:text-2xl" />
        </div>
        <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-[#3B9EE8] rounded-full animate-pulse" />
        <div className="hidden md:block absolute left-16 top-1/2 -translate-y-1/2 bg-[#0A1628] text-white text-xs font-500 px-3 py-2 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
          Chat met Shubam
          <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-[#0A1628]" />
        </div>
      </div>
    </a>
  );
}

interface PopupMessage {
  icon: string;
  title: string;
  subtitle: string;
  ctaLabel: string;
}

interface PromoPopupProps {
  msg: PopupMessage;
  onClose: () => void;
  onDisable: () => void;
}

function PromoPopup({ msg, onClose, onDisable }: PromoPopupProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [naam, setNaam] = useState('');
  const [email, setEmail] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const params = new URLSearchParams({ naam, email, bron: msg.title });
    try {
      await fetch(FORM_URL, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: params.toString() });
      setSubmitted(true);
      setTimeout(onClose, 2500);
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]" />
      <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4 pointer-events-none">
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.94, y: 20 }}
          transition={{ type: 'spring', stiffness: 320, damping: 28 }}
          className="pointer-events-auto bg-white rounded-2xl p-6 md:p-8 w-full max-w-sm md:max-w-md relative"
        >
          <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full text-[#0A1628]/40 hover:text-[#0A1628]/70 hover:bg-[#0A1628]/8 transition-all cursor-pointer">
            <i className="ri-close-line text-base" />
          </button>

          {submitted ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#3B9EE8]/15 mx-auto mb-4">
                <i className="ri-checkbox-circle-fill text-[#3B9EE8] text-2xl" />
              </div>
              <h3 className="font-heading font-700 text-[#0A1628] text-lg mb-1">Aanvraag ontvangen!</h3>
              <p className="text-[#0A1628]/50 text-sm">We reageren binnen 24 uur.</p>
            </div>
          ) : (
            <>
              <div className="mb-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#3B9EE8]/12 mb-4">
                  <i className={`${msg.icon} text-[#3B9EE8] text-lg`} />
                </div>
                <h3 className="font-heading font-700 text-[#0A1628] text-lg mb-1.5">{msg.title}</h3>
                <p className="text-[#0A1628]/55 text-sm leading-relaxed">{msg.subtitle}</p>
              </div>
              <form data-readdy-form id="offerte-aanvragen-form" onSubmit={handleSubmit} className="flex flex-col gap-3">
                <input type="text" name="naam" required placeholder="Jouw naam" value={naam} onChange={(e) => setNaam(e.target.value)} className="w-full border border-[#0A1628]/12 rounded-lg px-4 py-2.5 text-sm text-[#0A1628] placeholder-[#0A1628]/30 focus:outline-none focus:border-[#3B9EE8] transition-colors" />
                <input type="email" name="email" required placeholder="Je e-mailadres" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full border border-[#0A1628]/12 rounded-lg px-4 py-2.5 text-sm text-[#0A1628] placeholder-[#0A1628]/30 focus:outline-none focus:border-[#3B9EE8] transition-colors" />
                <button type="submit" disabled={submitting} className="w-full bg-[#3B9EE8] hover:bg-[#2D85C5] disabled:opacity-70 text-white font-600 text-sm py-3 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap">
                  {submitting ? 'Versturen...' : msg.ctaLabel}
                </button>
              </form>
              <p className="text-center text-[#0A1628]/25 text-xs mt-3">Geen spam &bull; Volledig gratis &bull; Vrijblijvend</p>
              <div className="mt-3 flex justify-center">
                <button onClick={onDisable} className="text-[#0A1628]/35 hover:text-[#0A1628]/60 text-xs underline underline-offset-2 cursor-pointer transition-colors">
                  Niet meer tonen dit bezoek
                </button>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </>
  );
}

function ScrollCTABar() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (dismissed) return;
      const scrolled = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      if (scrolled / total >= 0.55) setVisible(true);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [dismissed]);

  const handleDismiss = () => { setVisible(false); setDismissed(true); };
  const handleCTA = () => { handleDismiss(); const el = document.querySelector('#contact'); if (el) el.scrollIntoView({ behavior: 'smooth' }); };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 80, x: '-50%' }}
          animate={{ opacity: 1, y: 0, x: '-50%' }}
          exit={{ opacity: 0, y: 80, x: '-50%' }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="fixed bottom-36 md:bottom-8 left-1/2 z-[9985] bg-[#0A1628] rounded-xl px-4 py-3 flex items-center gap-3 w-[88vw] max-w-md border border-white/10"
          style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.35)' }}
        >
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#3B9EE8]/20 flex-shrink-0">
            <i className="ri-price-tag-3-line text-[#3B9EE8] text-sm" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-600 text-xs md:text-sm truncate">Klaar om goedkoper in te kopen?</p>
            <p className="text-white/40 text-xs hidden sm:block">Gratis prijsindicatie binnen 24 uur.</p>
          </div>
          <button
            onClick={handleCTA}
            className="bg-[#3B9EE8] hover:bg-[#2D85C5] text-white font-700 text-xs px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0"
          >
            Aanvragen
          </button>
          <button
            onClick={handleDismiss}
            className="w-7 h-7 flex items-center justify-center rounded-full text-white/40 hover:text-white hover:bg-white/10 cursor-pointer flex-shrink-0 transition-all"
            aria-label="Sluiten"
          >
            <i className="ri-close-line text-sm" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const POPUP_MESSAGES: Record<'scroll' | 'timer', PopupMessage> = {
  scroll: {
    icon: 'ri-question-answer-line',
    title: 'Benieuwd wat wij voor jou kunnen betekenen?',
    subtitle: 'DUC regelt de volledige inkoop uit China — van sourcing tot DDP levering. Ontvang gratis een prijsindicatie.',
    ctaLabel: 'Ja, stuur mij een offerte →',
  },
  timer: {
    icon: 'ri-rocket-line',
    title: 'Ben je klaar om goedkoper in te kopen?',
    subtitle: 'Gemiddeld besparen onze klanten 30–80% op inkoopkosten. Vraag nu een gratis en vrijblijvende prijscheck aan.',
    ctaLabel: 'Start met goedkoper inkopen →',
  },
};

export default function Popups() {
  const [activePopup, setActivePopup] = useState<'scroll' | 'timer' | null>(null);
  const scrollFiredRef = useRef(false);
  const timerFiredRef = useRef(false);

  const tryShowPopup = useCallback((type: 'scroll' | 'timer') => {
    if (isDisabled()) return;
    setActivePopup(type);
  }, []);

  // Trigger 1: Scroll 40% → "Benieuwd wat wij voor jou kunnen betekenen?"
  useEffect(() => {
    const handleScroll = () => {
      if (scrollFiredRef.current || activePopup) return;
      const scrolled = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      if (scrolled / total >= 0.4) {
        const alreadyShown = sessionStorage.getItem(SCROLL_POPUP_KEY) === '1';
        if (!alreadyShown) {
          scrollFiredRef.current = true;
          sessionStorage.setItem(SCROLL_POPUP_KEY, '1');
          tryShowPopup('scroll');
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [tryShowPopup, activePopup]);

  // Trigger 2: 5-minute timer → "Ben je klaar om goedkoper in te kopen?"
  useEffect(() => {
    if (timerFiredRef.current) return;
    const timer = setTimeout(() => {
      timerFiredRef.current = true;
      const alreadyShown = sessionStorage.getItem(TIMER_POPUP_KEY) === '1';
      if (!alreadyShown && !isDisabled()) {
        sessionStorage.setItem(TIMER_POPUP_KEY, '1');
        setActivePopup('timer');
      }
    }, 5 * 60 * 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = useCallback(() => setActivePopup(null), []);

  const handleDisable = useCallback(() => {
    try { sessionStorage.setItem(POPUP_DISABLED_KEY, '1'); } catch { /* ignore */ }
    setActivePopup(null);
  }, []);

  return (
    <>
      <WhatsAppButton />
      <ScrollCTABar />
      <AnimatePresence>
        {activePopup && (
          <PromoPopup
            key={activePopup}
            msg={POPUP_MESSAGES[activePopup]}
            onClose={handleClose}
            onDisable={handleDisable}
          />
        )}
      </AnimatePresence>
    </>
  );
}
